import {
  Box,
  Grid,
  HStack,
  Link,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useMemo } from 'react';

import { CompanyPeekTooltip } from '../company-details/CompanyPeekTooltip';
import type { CompanyMetrics } from '../hooks/useCompetitorComposition';
import { useCurrencyFormatter } from '../hooks/useCurrencyFormatter';
import type { EmployerHealth } from '../hooks/useEmployerHealth';
import type { HiddenGem } from '../hooks/useHiddenGems';
import type { RoleLevel } from '../hooks/useResolveProfileIds';

interface HiddenGemsCardProps {
  gems: HiddenGem[];
  isLoading: boolean;
  metrics?: CompanyMetrics[];
  healthData?: EmployerHealth[];
  userSalary?: number;
  userRoleLevel?: RoleLevel | null;
}

export const HiddenGemsCard = ({
  gems,
  isLoading,
  metrics,
  healthData,
  userSalary = 0,
  userRoleLevel,
}: HiddenGemsCardProps) => {
  const metricsMap = useMemo(
    () => new Map((metrics ?? []).map((m) => [m.companyId, m])),
    [metrics]
  );
  const healthMap = useMemo(
    () => new Map((healthData ?? []).map((h) => [h.companyId, h])),
    [healthData]
  );
  const salaryFormatter = useCurrencyFormatter();

  if (isLoading) {
    return <Skeleton height="160px" borderRadius="md" />;
  }

  if (gems.length === 0) {
    return null;
  }

  return (
    <Box
      bg="blue.50"
      p={4}
      borderRadius="md"
      border="1px solid"
      borderColor="blue.100"
    >
      <HStack justify="space-between" mb={3}>
        <Text fontSize="sm" fontWeight="semibold" color="blue.700">
          Hidden Gems
        </Text>
        <Text fontSize="xs" color="blue.600">
          Strong pay + culture without big-brand bias
        </Text>
      </HStack>

      <Grid
        templateColumns={`repeat(${Math.min(gems.length, 3)}, 1fr)`}
        gap={3}
      >
        {gems.map((gem) => (
          <Box
            key={gem.companyId}
            bg="white"
            p={3}
            borderRadius="md"
            border="1px solid"
            borderColor="blue.200"
          >
            <HStack spacing={1} mb={1} align="baseline">
              <CompanyPeekTooltip
                data={{
                  rcid: gem.companyId,
                  name: gem.shortName,
                  headcount: gem.headcount,
                  salary: gem.salary,
                  growthRate: metricsMap.get(gem.companyId)?.growthRate ?? null,
                  stabilityScore:
                    healthMap.get(gem.companyId)?.stabilityScore ?? null,
                  risk: healthMap.get(gem.companyId)?.risk ?? null,
                  sentimentOverall: gem.sentimentOverall,
                  linkedInUrl: gem.linkedInUrl,
                }}
                userSalary={userSalary}
                userRoleLevel={userRoleLevel ?? null}
              >
                <Text
                  fontWeight="bold"
                  fontSize="sm"
                  color="gray.800"
                  cursor="pointer"
                  _hover={{ textDecoration: 'underline' }}
                >
                  {gem.shortName}
                </Text>
              </CompanyPeekTooltip>
              {gem.linkedInUrl && (
                <Link
                  href={gem.linkedInUrl}
                  isExternal
                  fontSize="xs"
                  color="blue.400"
                  _hover={{ color: 'blue.600' }}
                  title="View on LinkedIn"
                >
                  <span role="img" aria-label="LinkedIn link">
                    🔗
                  </span>
                </Link>
              )}
            </HStack>

            {gem.salary !== null && (
              <Text fontSize="xs" color="gray.600" mb={2}>
                Median: {salaryFormatter(gem.salary)}
              </Text>
            )}

            <VStack align="start" spacing={1}>
              {gem.highlights.map((h) => (
                <HStack key={h} spacing={1.5} align="start">
                  <Text color="green.500" fontSize="xs" lineHeight="1.4">
                    ✓
                  </Text>
                  <Text fontSize="xs" color="gray.600" lineHeight="1.4">
                    {h}
                  </Text>
                </HStack>
              ))}
            </VStack>

            <Box mt={2} pt={2} borderTop="1px solid" borderColor="gray.100">
              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.500">
                  Score
                </Text>
                <Text fontSize="sm" fontWeight="bold" color="blue.600">
                  {gem.score}/100
                </Text>
              </HStack>
            </Box>
          </Box>
        ))}
      </Grid>
    </Box>
  );
};
